import { lazyWithRetry } from 'utils/helpers';
import qs from 'qs';
import {
  useState, useEffect, useCallback, Suspense,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Link, useParams } from 'react-router';
import { useTheme } from 'styled-components';
import { Popover, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import enumTypes from 'constants/enumTypes';
import {
  StyledIconFilter,
  StyledCollapseIcon,
  StyledSvgIconHover,
  StyledIconHoverBrand,
} from 'shared/icon/Icon.styled';
import { IconSvg } from 'shared/icon';
import {
  StyledBrandTemplateThumbnail,
  StyledIconWithTextWrapper,
  StyledIconWithText,
  StyledParagraph,
  StyledMainSettings,
  StyledTopMainSettings,
  StyledCountryLang,
  StyledStatusChange,
  StyledStatus,
  StyledStatusName,
  StyledBorderTop,
  StyledImgHoverIcons,
  StyledHoverIcons,
  StyledCollapseWrapper,
  StyledCardPartnerWrapper,
  StyledCollapseIconsWrapper,
  StyledCardBrandNameMain,
  StyledBrandCardMainConfig,
  StyledCardItemStatus,
} from 'features/brands/components/Brand.styled';
import { useTimeZone } from 'hooks';
import { AppConstants } from 'constants/index';

import { useCreateOrEditUserConfigMutation } from 'app/userPreferences/api';
import { selectUserConfigBrandSettingsExpand } from 'app/userPreferences/selectors';
import { useAppSelector } from 'store/hooks';
import { makeSelectLocaleResource } from 'app/locale/selectors';
import { deviceTypes as deviceTypesOrder } from 'constants/mappingOrder';
import {
  StyledMaintenancePeriod, StyledMaintenancePeriodDates, StyledSkeleton, StyledTruncate,
} from 'shared/Common.styled';
import Config from 'config';
import { resource, types } from 'constants/accessControl';
import { Can } from 'shared/permission';
import withDefaultProps from 'shared/withDefaultProps';
import { useGetTimeZonesQuery } from 'app/timeZone/api';
import { useGenerateLaunchTokenMutation } from 'app/auth/api';

const ChangeMainConfigs = lazyWithRetry(() => import('containers/brandSetting/ChangeMainConfigs'));
const ChangeStatus = lazyWithRetry(() => import('containers/brandSetting/ChangeStatus'));
const ChangeTimeZone = lazyWithRetry(() => import('containers/brandSetting/ChangeTimeZone'));
const selectLocale = makeSelectLocaleResource(
  {
    brandStatuses: enumTypes.brandStatuses,
    deviceTypes: enumTypes.deviceTypes,
  },
  {
    deviceTypes: deviceTypesOrder,
  },
);
const MainInfo = (props) => {
  const {
    brand: {
      id, name, licenseId, templateId, createdDate, status, market, disabledVersionNames, timeZone: brandTimeZone, domain, partnerId, maintenancePeriodFrom, maintenancePeriodTo,
    },
    getBrand,
    main,
    getMarkets,
    markets,
    getTemplates,
    resetTemplates,
    templates,
    resetBrand,
    resetBrandSettings,
    callEndedStatus,
    changeBrandCallEndedStatus,
  } = props;

  const { colors } = useTheme();
  const [createOrEditUserConfig] = useCreateOrEditUserConfigMutation();
  const expand = useAppSelector(selectUserConfigBrandSettingsExpand);
  const locale = useAppSelector(selectLocale);
  const { data: timeZones } = useGetTimeZonesQuery();

  const { brandStatuses = {}, deviceTypes = {} } = locale;

  const { getFormattedDate } = useTimeZone();

  const { t } = useTranslation();

  const params = useParams();

  const [action, setAction] = useState({ type: '', visible: false });

  useEffect(() => {
    getTemplates();
  }, [getTemplates]);

  useEffect(() => {
    if (params.id) {
      getBrand(params.id, true);
    }
  }, [getBrand, params.id]);

  useEffect(
    () => () => {
      resetBrand();
      resetBrandSettings();
      resetTemplates();
    },
    [resetBrand, resetBrandSettings, resetTemplates],
  );

  useEffect(() => {
    getMarkets();
  }, [getMarkets]);

  const onClose = useCallback(() => {
    setAction({
      type: '',
      visible: false,
    });
  }, []);

  useEffect(() => {
    if (callEndedStatus) {
      onClose();
      changeBrandCallEndedStatus(false);
      getBrand(params.id, true);
    }
  }, [callEndedStatus, onClose, changeBrandCallEndedStatus, params.id, getBrand]);

  const template = templates.find((el) => el.id === templateId);

  const getStatus = () => {
    if (status !== enumTypes.brandStatuses.VersionNamesDisabled) {
      return brandStatuses[status];
    }
    return `${disabledVersionNames.map((el) => deviceTypes.get(el)).join(', ')} ${t('disabled')}`;
  };

  const isExpand = !_.isUndefined(expand) ? expand : true;

  const handleToggle = () => {
    createOrEditUserConfig(
      {
        key: 'brandSettingsExpand',
        configValue: !expand,
        skipGlobalLoading: true,
      },
    );
  };

  const getTimeZone = () => {
    if (brandTimeZone && timeZones?.length) {
      const data = timeZones.find((el) => el.id === brandTimeZone);
      return data && data.name;
    }
    return null;
  };

  const timeZone = getTimeZone();

  const actionTypes = {
    statusChange: () => <ChangeStatus visible={action.visible} onClose={onClose} />,
    timeZoneChange: () => <ChangeTimeZone visible={action.visible} onClose={onClose} />,
    mainConfigsChange: () => <ChangeMainConfigs visible={action.visible} onClose={onClose} />,
  };

  const Modal = action.type ? actionTypes?.[action.type]?.() : null;

  const [generateLaunchToken] = useGenerateLaunchTokenMutation();

  const navigateBuilder = async () => {
    const { launchToken } = await generateLaunchToken().unwrap();
    const query = {
      cid: partnerId,
      p: id,
    };
    const url = `${Config.siteBuilderURL}edit/customize/${id}?${qs.stringify(query)}&code=${launchToken}`;
    window.open(url, '_blank');
  };

  const renderMainConfigs = (isSmallSize) => name && (
    <StyledBrandCardMainConfig $isSmallSize={isSmallSize}>
      <StyledCardBrandNameMain $isSmallSize={isSmallSize}>{name}</StyledCardBrandNameMain>
      <StyledIconWithText>
        <IconSvg icon="BrandsVerificationIcon" width={14} height={14} />
        <StyledParagraph>
          {`${t('licenseId')}:`}
          <Popover placement="bottomLeft" content={licenseId} title="" rootClassName="description-popover">
            <StyledTruncate>
              <strong>{licenseId || '-'}</strong>
            </StyledTruncate>
          </Popover>
        </StyledParagraph>
        <Can type={types.edit} rule={`${resource.brands}.${resource.mainSettings}`}>
          <StyledSvgIconHover
            onClick={() => {
              setAction({
                type: 'mainConfigsChange',
                visible: true,
              });
            }}
          >
            <Tooltip title={t('edit')}>
              <IconSvg icon="EditIcon" width={12} height={12} color={colors.blue} />
            </Tooltip>
          </StyledSvgIconHover>
        </Can>
      </StyledIconWithText>
    </StyledBrandCardMainConfig>
  );

  const renderStatus = () => (
    <StyledStatus $isExpand={isExpand}>
      <StyledCardItemStatus $isActive={status === enumTypes.brandStatuses.Active} />
      <StyledStatusName>
        <span>{id && getStatus()}</span>
        {!_.isEmpty(maintenancePeriodFrom) && (
          <StyledMaintenancePeriod>
            <IconSvg icon="MaintenanceIcon" width="1.14rem" height="1rem" color="" />
            <span>
              {`${t('brand:maintenancePeriod')}: `}
              <strong>
                <StyledMaintenancePeriodDates>
                  {`${getFormattedDate(maintenancePeriodFrom, AppConstants.format.dateTime)} - `}
                  {maintenancePeriodTo ? getFormattedDate(maintenancePeriodTo, AppConstants.format.dateTime) : <IconSvg icon="InfinityIcon" width="16" height="16" />}
                </StyledMaintenancePeriodDates>
              </strong>
            </span>
          </StyledMaintenancePeriod>
        )}
      </StyledStatusName>
      <Can type={types.edit} rule={`${resource.brands}.${resource.mainSettings}`}>
        <StyledStatusChange
          onClick={() => {
            setAction({
              type: 'statusChange',
              visible: true,
            });
          }}
        >
          <Tooltip title={t('edit')}>
            <div>
              <IconSvg icon="EditIcon" width={12} height={12} color={colors.blue} />
            </div>
          </Tooltip>
        </StyledStatusChange>
      </Can>
    </StyledStatus>
  );

  const renderActions = () => (isExpand ? (
    <StyledImgHoverIcons>
      <StyledHoverIcons>
        <Link to={`//${domain}`} target="_blank">
          <IconSvg icon="PreviewIcon" />
        </Link>
      </StyledHoverIcons>
      <StyledHoverIcons>
        <StyledIconHoverBrand onClick={navigateBuilder}>
          <IconSvg icon="BrandBuilderIcon" />
        </StyledIconHoverBrand>
      </StyledHoverIcons>
    </StyledImgHoverIcons>
  ) : (
    <StyledCollapseIconsWrapper>
      <div>
        <Tooltip title={t('view')}>
          <Link to={`//${domain}`} target="_blank">
            <IconSvg icon="PreviewIcon" />
          </Link>
        </Tooltip>
      </div>
      <div>
        <Tooltip title="brand:builder">
          <StyledIconHoverBrand onClick={navigateBuilder}>
            <IconSvg icon="BrandBuilderIcon" />
          </StyledIconHoverBrand>
        </Tooltip>
      </div>
    </StyledCollapseIconsWrapper>
  ));

  return (
    <StyledBorderTop>
      {action.visible && <Suspense fallback={<span />}>{Modal}</Suspense>}
      <StyledCollapseIcon onClick={handleToggle}>
        <Tooltip title={t(isExpand ? 'collapse' : 'expand')}>
          <StyledIconFilter $isExpand={isExpand}>
            <IconSvg icon="DropdownIcon" />
          </StyledIconFilter>
        </Tooltip>
      </StyledCollapseIcon>
      {isExpand ? (
        <StyledMainSettings>
          <StyledBrandTemplateThumbnail>
            {template ? (
              <img alt="" src={template ? template.thumbnailUrl : ''} />
            ) : (
              <StyledSkeleton title={false} paragraph={false} active avatar={{ shape: 'square' }} $avatarWidth={220} $avatarHeight={148} />
            )}
            {renderActions()}
          </StyledBrandTemplateThumbnail>
          <StyledTopMainSettings>
            {name ? (
              <>
                {renderMainConfigs()}
                {renderStatus()}
                <StyledIconWithTextWrapper>
                  <StyledCountryLang>
                    <StyledIconWithText>
                      <IconSvg icon="InfoIcon" width={13} height={13} />
                      <StyledParagraph>
                        <Popover placement="bottomLeft" content={t('id')} title="" rootClassName="description-popover">
                          <StyledTruncate>
                            <span>{`${t('id')}:`}</span>
                          </StyledTruncate>
                        </Popover>
                        <strong>{id}</strong>
                      </StyledParagraph>
                    </StyledIconWithText>
                    <StyledIconWithText>
                      <IconSvg icon="Calendar2Icon" width={13} height={13} />
                      <StyledParagraph>
                        <Popover placement="bottomLeft" content={t('registrationDate')} title="" rootClassName="description-popover">
                          <StyledTruncate>
                            <span>{`${t('registrationDate')}:`}</span>
                          </StyledTruncate>
                        </Popover>
                        <strong>{createdDate && getFormattedDate(createdDate)}</strong>
                      </StyledParagraph>
                    </StyledIconWithText>
                    <StyledIconWithText>
                      <IconSvg icon="LocationIcon" width={13} height={13} />
                      <StyledParagraph>
                        {`${t('country')}:`}
                        <Popover placement="bottomLeft" content={main.country.name} title="" rootClassName="description-popover">
                          <StyledTruncate>
                            <strong>{main.country.name}</strong>
                          </StyledTruncate>
                        </Popover>
                      </StyledParagraph>
                    </StyledIconWithText>
                    <StyledIconWithText>
                      <IconSvg icon="TimeZoneIcon" width={13} height={13} />
                      <StyledParagraph>
                        <Popover placement="bottomLeft" content={t('timeZone')} title="" rootClassName="description-popover">
                          <StyledTruncate>
                            <span>{`${t('timeZone')}:`}</span>
                          </StyledTruncate>
                        </Popover>
                        <Popover placement="bottomLeft" content={timeZone} title="" rootClassName="description-popover">
                          <StyledTruncate>
                            <strong>{timeZone}</strong>
                          </StyledTruncate>
                        </Popover>
                      </StyledParagraph>
                      <Can type={types.edit} rule={`${resource.brands}.${resource.mainSettings}`}>
                        <StyledStatusChange
                          onClick={() => {
                            setAction({
                              type: 'timeZoneChange',
                              visible: true,
                            });
                          }}
                        >
                          <Tooltip title={t('edit')}>
                            <div>
                              <IconSvg icon="EditIcon" width={12} height={12} color={colors.blue} />
                            </div>
                          </Tooltip>
                        </StyledStatusChange>
                      </Can>
                    </StyledIconWithText>
                    <StyledIconWithText>
                      <IconSvg icon="LanguageIcon" width={12} height={12} />
                      <StyledParagraph>
                        {t('language')}
                        :
                        <span>
                          <strong>{main.language.name}</strong>
                        </span>
                      </StyledParagraph>
                    </StyledIconWithText>
                    <StyledIconWithText>
                      <IconSvg icon="CurrencyIcon" width={16} height={16} />
                      <StyledParagraph>
                        {t('currency')}
                        :
                        <span>
                          <strong>{main.currency.id}</strong>
                        </span>
                      </StyledParagraph>
                    </StyledIconWithText>
                    {!!markets.length && market && markets[0].id !== market.id && (
                      <StyledIconWithText>
                        <IconSvg icon="LicenseIcon" width={13} height={13} />
                        <StyledParagraph>
                          {t('brand:market')}
                          :
                          <span>
                            <strong>{market.name}</strong>
                          </span>
                        </StyledParagraph>
                      </StyledIconWithText>
                    )}
                  </StyledCountryLang>
                </StyledIconWithTextWrapper>
              </>
            ) : (
              <StyledSkeleton paragraph={{ rows: 2, width: [100, '100%'] }} title={{ width: 300 }} $titleHeight={28} active />
            )}
          </StyledTopMainSettings>
        </StyledMainSettings>
      ) : (
        <StyledCollapseWrapper>
          <StyledCardPartnerWrapper>
            {name ? (
              <>
                {renderMainConfigs(true)}
                {renderStatus()}
              </>
            ) : (
              <StyledSkeleton paragraph={false} title={{ width: 400 }} active />
            )}
          </StyledCardPartnerWrapper>
          <div>{renderActions()}</div>
        </StyledCollapseWrapper>
      )}
    </StyledBorderTop>
  );
};

MainInfo.propTypes = {
  brand: PropTypes.object.isRequired,
  getBrand: PropTypes.func.isRequired,
  resetBrand: PropTypes.func.isRequired,
  resetBrandSettings: PropTypes.func.isRequired,
  main: PropTypes.object.isRequired,
  markets: PropTypes.array,
  getMarkets: PropTypes.func.isRequired,
  templates: PropTypes.array,
  getTemplates: PropTypes.func.isRequired,
  resetTemplates: PropTypes.func.isRequired,
  callEndedStatus: PropTypes.bool,
  changeBrandCallEndedStatus: PropTypes.func.isRequired,
};

MainInfo.defaultProps = {
  markets: [],
  templates: [],
  callEndedStatus: false,
};

export default withDefaultProps(MainInfo, MainInfo.defaultProps);
